// ** React Imports
import { useContext, Fragment, useEffect } from 'react'
import { Link, useHistory } from 'react-router-dom'

// ** Custom Hooks
import { useSkin } from '@hooks/useSkin'
import useJwt from '@src/auth/jwt/useJwt'
import { useTranslation } from 'react-i18next'

// ** Third Party Components
import { useDispatch, useSelector } from 'react-redux'
import { toast, Slide } from 'react-toastify'
import { useForm, Controller } from 'react-hook-form'
import {HelpCircle, Coffee } from 'react-feather'

// ** Actions
import { handleLogin } from '@store/authentication'
import { fetchClient } from '@store/client'

// ** Context
import { AbilityContext } from '@src/utility/context/Can'
import * as ozpAbility from '@src/configs/acl/ozpAbility'

// ** Custom Components
import Avatar from '@components/avatar'
import InputPasswordToggle from '@components/input-password-toggle'

// ** Utils
import { getHomeRouteForLoggedInUser } from '@utils'

// ** Reactstrap Imports
import { Row, Col, Form, Input, Label, Alert, Button, CardText, CardTitle, UncontrolledTooltip } from 'reactstrap'

// ** Styles
import '@styles/react/pages/page-authentication.scss'

//const ToastContent = ({ name, role }) => {
const ToastContent = ({ name }) => {
  // ** Hooks
  const { t } = useTranslation()

  return (
    <Fragment>
    <div className='toastify-header'>
      <div className='title-wrapper'>
        <Avatar size='sm' color='success' icon={<Coffee size={12} />} />
        <h6 className='toast-title fw-bold'>{t("Welcome")}, {name}</h6>
      </div>
    </div>
    <div className='toastify-body'>
      <span>{t("You have successfully logged in to Optima HR!")}</span>
    </div>
  </Fragment>
  )
  }

const defaultValues = {
  password: '',
  loginEmail: ''
}

const Login = () => {
  // ** Hooks
  const { skin } = useSkin()
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const history = useHistory()
  const ability = useContext(AbilityContext)
  const client = useSelector(state => state.client.clientData)

  useEffect(() => {
//    dispatch(fetchClient(window.location.hostname))
      dispatch(fetchClient())
  }, [])

  const {
    control,
    setError,
    handleSubmit,
    formState: { errors }
  } = useForm({ defaultValues })

  const illustration = skin === 'dark' ? 'background_violet-small.jpg' : 'background_yellow-small.jpg',
    source = client?.background || require(`@src/assets/images/pages/${illustration}`).default,
    logo = client?.flagimage || require('@src/assets/images/logo/logo.png').default

  const ozpsource = skin === 'dark' ? 'opt-trans-white-big.png' : 'opt-trans-black-big.png',
  ozplogo = require(`@src/assets/images/pages/${ozpsource}`).default


  const onSubmit = data => {
    if (Object.values(data).every(field => field.length > 0)) {
      useJwt
        .login({ 
          username: data.loginEmail, 
          password: data.password,
          host: window.location.hostname
         })
        .then(res => {
            const profiles = res.data.profile.split('§')
            const userAbilities = []
            userAbilities.push(...ozpAbility.basicAbility)
            if (profiles.includes("Adm")) {
                userAbilities.push(...ozpAbility.adminAbility)
        //        userAbilities.push(...ozpAbility.superAdmin)
//                console.log("admin")
            }
            if (profiles.includes("Acqu")) {
                userAbilities.push(...ozpAbility.acquireAbility)
            }
            if (profiles.includes("Empl")) {
                userAbilities.push(...ozpAbility.employeesAbility)
            }
            if (profiles.includes("Com")) {
                userAbilities.push(...ozpAbility.companyAbility)
            }
            if (profiles.includes("Ana")) {
                userAbilities.push(...ozpAbility.analyticAbility)
            }
            if (profiles.includes("Bud")) {
                userAbilities.push(...ozpAbility.budgetAbility)
            }

          const data = { 
            ...res.data,
            ability: userAbilities
          }
          dispatch(handleLogin(data))
          ability.update(data.ability)
          history.push(getHomeRouteForLoggedInUser(data.role))
          toast.success(
            <ToastContent name={data.fullname || data.username || ''} role={data.role || 'admin'} />,
            { icon: false, transition: Slide, hideProgressBar: true, autoClose: 2000 }
          )
        })
        .catch(err => {
          const { response } = err
          if (response && response.status === 401) {
            toast.error("Votre nom d'utilisateur ou votre mot de passe est incorrect.")
          } else {
              toast.error(
                <>Une erreur est survenue !</>,
                { icon: false, transition: Slide, hideProgressBar: true, autoClose: 2000 }
              )
          }
        })
    } else {
      for (const key in data) {
        if (data[key].length === 0) {
          setError(key, {
            type: 'manual'
          })
        }
      }
    }
  }

  if (!client) {
    return <>Loading...</>
  }

  return (
    <div className='auth-wrapper auth-cover'>
      <Row className='auth-inner m-0'>
        <Link className='brand-logo' to='/' onClick={e => e.preventDefault()}>
          <img height="26" width="26" src={logo} alt="logo"/>
          {client.name ? <h2 className='brand-text ms-1'>{client.name}</h2> : <h2 className='brand-text ms-1'>OPTIM<span style={{color: "#0CC2AA"}}>A</span> HR</h2>}
        </Link>
        <Col className='d-none d-lg-flex align-items-center p-0' lg='8' sm='12'>
          <img style={{ height: '100vh' }} src={source} alt='Login Cover' />
        </Col>
        <Col className='d-flex align-items-center auth-bg px-2 p-lg-5' lg='4' sm='12'>
          <Col className='px-xl-2 mx-auto' sm='8' md='6' lg='12'>
            <CardTitle tag='h2' className='fw-bold mb-1'>
            {t("Welcome to ")}<img width="150px" src={ozplogo} alt="OZP Logo"/>
            </CardTitle>
            <CardText className='mb-2'>{t("Please sign-in to your account")}</CardText>
            <Form className='auth-login-form mt-2' onSubmit={handleSubmit(onSubmit)}>
              <div className='mb-1'>
                <Label className='form-label' for='login-email'>
                {t("User")}
                </Label>
                <Controller
                  id='loginEmail'
                  name='loginEmail'
                  control={control}
                  render={({ field }) => (
                    <Input
                      autoFocus
                      type='email'
                      placeholder='john@example.com'
                      invalid={errors.loginEmail && true}
                      {...field}
                    />
                  )}
                />
              </div>
              <div className='mb-1'>
                <div className='d-flex justify-content-between'>
                  <Label className='form-label' for='login-password'>
                  {t("Password")}
                  </Label>
                  <Link to='/forgot-password'>
                    <small>{t("Forgot Password?")}</small>
                  </Link>
                </div>
                <Controller
                  id='password'
                  name='password'
                  control={control}
                  render={({ field }) => (
                    <InputPasswordToggle className='input-group-merge' invalid={errors.password && true} {...field} />
                  )}
                />
              </div>
              <div className='form-check mb-1'>
                <Input type='checkbox' id='remember-me' />
                <Label className='form-check-label' for='remember-me'>
                {t("Remember Me")}
                </Label>
              </div>
              <Button type='submit' color='primary' block>
              {t("Sign in")}
              </Button>
            </Form>
          </Col>
        </Col>
      </Row>
    </div>
  )
}

export default Login
